import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { type SessionFile } from "@/shared/domain/sessions/SessionFile";
import { type PaginatedResponse } from "@/shared/http";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSessionFilesParams {
  sessionId: string;
  pageToken?: string;
  /** Server page size; the backend defaults to 100 when omitted. */
  limit?: number;
}

/**
 * Fetches one page of file contributions for a Session.
 *
 * Each row is a wire-format `SessionFile` view: the contribution's
 * `file_id` and optional time-range pair joined server-side with the
 * file's display fields (name, dataset, created/modified, tags, size,
 * origination, path), so consumers render file details without
 * per-file record fetches.
 */
export function useSessionFiles({
  sessionId,
  pageToken,
  limit,
}: UseSessionFilesParams): UseQueryResult<
  PaginatedResponse<SessionFile>,
  Error
> {
  const { sessionService } = useDomainServices();
  const orgId = useCurrentOrgId();
  return useQuery<PaginatedResponse<SessionFile>, Error>({
    queryKey: sessionQueryKeys.files(sessionId, pageToken, limit),
    queryFn: ({ signal }) =>
      sessionService.listSessionFiles(sessionId, {
        abortSignal: signal,
        limit,
        pageToken,
        resourceOwnerId: orgId,
      }),
  });
}
